/**
 * The right-side edit drawer every service-page card opens (DNKADM-0493). One Save and one
 * Cancel in the footer — the only Save on the page. The caller owns what Save does: it
 * returns `true` to close (success), `false` to stay open (a failure the drawer's content
 * already shows in plain words, or a dismissed confirm).
 *
 * `doneOnly` is the one exception, for the Special prices drawer: its editors save one row
 * at a time (each day, date or coach is its own write), so the footer is a single Done that
 * closes it. Same shell and width as the calendar drawers (`BlockedDrawer`).
 */
import { useId, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Drawer, Stack, Typography } from '@mui/material';

export interface EditDrawerProps {
  open: boolean;
  title: string;
  /** Close without saving (Cancel, Escape, backdrop, or Done). */
  onClose: () => void;
  /** Run the save. Resolve `true` to close, `false` to stay open. Omitted with `doneOnly`. */
  onSave?: () => Promise<boolean>;
  /** Hold Save off (e.g. a part of the form could not load, so nothing may be written). */
  saveDisabled?: boolean;
  /** A footer of one Done button instead of Save + Cancel (see the file header). */
  doneOnly?: boolean;
  children: ReactNode;
}

export function EditDrawer({
  open,
  title,
  onClose,
  onSave,
  saveDisabled = false,
  doneOnly = false,
  children,
}: EditDrawerProps) {
  const titleId = useId();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!onSave) return;
    setError(null);
    setSaving(true);
    try {
      const ok = await onSave();
      if (ok) onClose();
    } catch {
      // The parts report their own errors; this is the backstop for anything they did not.
      setError('The changes couldn’t be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: { width: 520, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      {open && (
        <>
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography id={titleId} variant="h6" component="h2">
              {title}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
            {children}
            {error && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ px: 3, py: 1.5, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}
          >
            {doneOnly ? (
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            ) : (
              <>
                <Button onClick={onClose} disabled={saving} color="inherit">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void handleSave()}
                  disabled={saving || saveDisabled}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
}
